/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { AuxiliaryOutput, ccHubFeature, CChubState } from './cchub.reducer';
import { AudioDevicesRoles } from 'web-audio-module';
import { AudioDevicesRolesInternal } from 'web-audio-module/dist/e-audio-devices-roles.enum';
import { pipe } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { selectIsUsbAttachedAndHeadsetDisconnected } from '../../usb/+state/usb.selectors';
import { CcHubAudioService } from '../services/cc-hub-audio.service';
import { selectAgentStatus } from '../../user/+state/user.selectors';
import { AgentStatus } from "CalltakingCoreApi";

export const selectCCHubState = createFeatureSelector<CChubState>(ccHubFeature.name);

export const selectCCHubEffectInitialized = createSelector(selectCCHubState, (state) => state.effectsInitialized);

export const selectCcHubCallAudioInputs = createSelector(selectCCHubState, (state) => state.inputs);

export const selectCCHubCallAudioOutputs = createSelector(selectCCHubState, (state) => state.outputs);

export const selectConnectedCcHubCallAudioInputs = createSelector(selectCcHubCallAudioInputs, (devices) =>
    devices.filter((input) => input.connected)
);
export const selectConnectedCcHubCallAudioOutputs = createSelector(selectCCHubCallAudioOutputs, (devices) =>
    devices.filter((input) => input.connected)
);

export const selectCcHubCallAudioInputConnected = (peripheral: string) => createSelector(selectCcHubCallAudioInputs, (inputs) =>
    Boolean(inputs.find((input) => input.id === peripheral && input.connected))
);

export const selectConnectedCcHubDefaultAudioOutputs = createSelector(selectConnectedCcHubCallAudioOutputs, (devices) =>
    devices.filter((device) => CcHubAudioService.DEFAULT_AUDIO_OUTPUT_PERIPHERALS.includes(device.id as AudioDevicesRoles | AudioDevicesRolesInternal))
);

export const selectCcHubRecorderAudioOutputs = createSelector(selectCCHubCallAudioOutputs, (devices) =>
    devices.filter((device) => CcHubAudioService.RECORDER_AUDIO_OUTPUT_PERIPHERALS.includes(device.id as AudioDevicesRoles | AudioDevicesRolesInternal))
);

export const selectCcHubAudioOutputConnected = (peripheral: string) => createSelector(selectCCHubCallAudioOutputs, (outputs) =>
    Boolean(outputs.find((output) => output.id === peripheral && output.connected))
);

export const selectCCHubHeadsetOverride = createSelector(selectCCHubState, (state) => state.ccHubHeadsetOverride);

export const selectCCHubAttached = createSelector(selectCCHubState, (state) => state.ccHubAttached);

export const selectCCHubMuteStatus = createSelector(selectCCHubState, (state) => state.muteStatus);

export const selectCCHubInputMuted = (peripheral: string) =>
    createSelector(selectCCHubMuteStatus, (muteStatus) => muteStatus[peripheral]?.muted);

export const selectCCHubInputMuteOnAnswer = (peripheral: string) =>
    createSelector(selectCCHubMuteStatus, (muteStatus) => Boolean(muteStatus[peripheral]?.muteOnAnswer));

export const selectCCHubVolumeStatus = createSelector(selectCCHubState, (state) => state.volumeStatus);

export const selectCCHubPeripheralVolume = (peripheral: string) =>
    createSelector(selectCCHubVolumeStatus, (volumeStatus) => volumeStatus[peripheral] ? volumeStatus[peripheral].volume : 5);


export const selectCCHubSerialNumber = createSelector(selectCCHubState, (state) => state.ccHubSn);

export const selectCCHubInitialized = createSelector(selectCCHubState, (state) => state.ccHubInitialized);

export const selectFirmwareVersion = createSelector(selectCCHubState, (state) => state.firmwareVersion);

export const selectCCHubAttachedAndInitialized = createSelector(
    selectCCHubAttached,
    selectCCHubInitialized,
    (connected, initialized) => connected && initialized
);

export const selectCCHubHeadset1Connected = createSelector(selectCCHubState, (state) => state.deviceState['HEADSET1_JS']?.state);

export const selectCCHubHeadset2Connected = createSelector(selectCCHubState, (state) => state.deviceState['HEADSET2_JS']?.state);

export const _selectRadioHeadsetConnected = createSelector(selectCCHubState, (state) => state.deviceState['EPE_PTT']?.state);

// workaround for external radio headset presence detection, use EPE state to signal radio headset is 'plugged' or 'unplugged' corresponding to 'pressed' and 'released'
// Note: RPI should be ignored/excluded from call if HEADSET_1 is plugged.
export const selectRadioHeadsetConnected = createSelector(
    _selectRadioHeadsetConnected,
    selectCCHubHeadset1Connected,
    (rpiHeadset, headset1) => !headset1 && rpiHeadset
);

const _selectRpiExternalJackSenseState = createSelector(
    selectCCHubAttachedAndInitialized,
    selectCCHubHeadset1Connected,
    (ccHubConnectedAndInitialized, ccHubHeadsetDetected) => (ccHubConnectedAndInitialized ? ccHubHeadsetDetected : undefined)
);

export const selectRpiExternalJackSenseState = pipe(
    select(_selectRpiExternalJackSenseState),
    filter((val) => val !== undefined),
    map((val) => val)
);

export const selectCCHubHeadsetConnected = createSelector(
    selectCCHubHeadset1Connected,
    selectCCHubHeadset2Connected,
    selectRadioHeadsetConnected,
    selectCCHubHeadsetOverride,
    (ccHubHeadset1Connected, ccHubHeadset2Connected, radioHeadsetConnected, override) => radioHeadsetConnected || ccHubHeadset1Connected || ccHubHeadset2Connected || override
);

export const selectHeadsetDisconnectedAndTimedOut = createSelector(
    selectCCHubState,
    (state) => state.headsetDisconnectedAndTimedOut
);

export const selectIsHubConnectedAndHeadsetDisconnected = createSelector(
    selectCCHubAttached,
    selectCCHubHeadsetConnected,
    (hubAttached, headsetConnected) => hubAttached && !headsetConnected
);

export const selectIsDeviceConnectedAndHeadsetDisconnected = createSelector(
    selectIsHubConnectedAndHeadsetDisconnected,
    selectIsUsbAttachedAndHeadsetDisconnected,
    (hubDisconnected, usbDisconnected) => hubDisconnected || usbDisconnected
);

export const selectAlertOutputToDevice = createSelector(selectCCHubState, (state) => state.alertToDevice);
export const selectAlertOutputToHeadsets = createSelector(selectCCHubState, (state) => state.alertToHeadsets);
export const selectAlertOutputEnabled = (peripheral: string) => createSelector(selectConnectedCcHubDefaultAudioOutputs, selectAlertOutputToHeadsets, (outputs, alertToHeadsets) =>
    Boolean(outputs.find((output) => output.id === peripheral) && alertToHeadsets));
export const selectIrrOutputToDevice = createSelector(selectCCHubState, (state) => state.irrToDevice);
export const selectIrrOutputToHeadsets = createSelector(selectCCHubState, (state) => state.irrToHeadsets);
export const selectIrrOutputEnabled = (peripheral: string) => createSelector(selectConnectedCcHubDefaultAudioOutputs, selectIrrOutputToHeadsets, (outputs, irrToHeadsets) =>
    Boolean(outputs.find((output) => output.id === peripheral) && irrToHeadsets));

export const selectAuxAgentStatus = createSelector(selectCCHubState, (state) => state.auxiliaryAgentStatusMap);

export const selectAuxRelayState = (auxiliaryOutput: AuxiliaryOutput) => createSelector(selectAuxAgentStatus, selectAgentStatus, selectCCHubHeadsetConnected, (statusMap, status, headsetConnected) =>
    isAgentStatusApplicable(statusMap[auxiliaryOutput], status) || statusMap[auxiliaryOutput] === 'HEADSET_DISCONNECTED' && !headsetConnected
);

function isAgentStatusApplicable(auxStatus: AgentStatus | 'HEADSET_DISCONNECTED', status: AgentStatus) {
    switch (status) {
        case 'ON_CALL':
        case 'HOLDING_CALL':
        case 'ON_ACD_CALL':
            return auxStatus === 'ON_CALL';
        default:
            return auxStatus === status;
    }
}

export const selectAncillaryVersionNumber = createSelector(selectCCHubState, (state) => state.versionNumber);
